/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api/usersApi.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    API functions for Users module.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders, API_CONFIG } from '../../../config/api';
import { ListUsersResponse, UserResponse, User } from '../types/users.types';
import { uploadAvatar } from '../../../lib/api';

export interface FetchUsersParams {
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  search?: string;
}

export interface CreateUserData {
  username: string;
  email: string;
  password?: string;
  fullName?: string;
  department?: string;
  locale: string;
  timezone: string;
  startupDialog: string;
  totpEnabled: boolean;
  isActive: boolean;
  debugMode: boolean;
  profileId?: string;
  logProfileId?: string;
  extensionId?: number | string | null;
  tenantIds: string[];
  avatar?: string;
}

export interface UpdateUserData extends Partial<CreateUserData> {
  password?: string;
}

/**
 * Fetch users list with pagination and filters
 */
export async function fetchUsers(
  params: FetchUsersParams,
  tenantSlug: string,
  userId?: string | number | null,
): Promise<{ data: User[]; pagination: ListUsersResponse['pagination'] }> {
  const queryParams = new URLSearchParams({
    page: params.page.toString(),
    limit: params.limit.toString(),
    sortBy: params.sortBy,
    sortOrder: params.sortOrder,
    ...(params.search && { search: params.search }),
  });

  const response = await fetch(`${buildApiUrl(API_CONFIG.ENDPOINTS.USERS)}?${queryParams}`, {
    method: 'GET',
    headers: getApiHeaders(tenantSlug, userId),
  });

  if (!response.ok) {
    const message = await response.text().catch(() => '');
    throw new Error(message || response.statusText);
  }

  const payload: ListUsersResponse = await response.json();

  return {
    data: Array.isArray(payload?.data) ? payload.data : [],
    pagination: payload?.pagination ?? {
      page: params.page,
      limit: params.limit,
      total: 0,
      pages: 0,
    },
  };
}

/**
 * Fetch user by ID
 */
export async function fetchUserById(
  id: string,
  tenantSlug: string,
  userId?: string | number | null,
): Promise<User> {
  const response = await fetch(buildApiUrl(`${API_CONFIG.ENDPOINTS.USERS}/${id}`), {
    method: 'GET',
    headers: getApiHeaders(tenantSlug, userId),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage = errorData.error || errorData.message || `HTTP error! status: ${response.status}`;
    throw new Error(errorMessage);
  }

  const result: UserResponse = await response.json();
  return result.data;
}

/**
 * Create new user
 */
export async function createUser(
  data: CreateUserData,
  tenantSlug: string,
  userId?: string | number | null,
): Promise<User> {
  const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.USERS), {
    method: 'POST',
    headers: getApiHeaders(tenantSlug, userId),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage = errorData.message || errorData.error || 'Failed to create user';
    // Include validation errors if available
    if (errorData.issues && Array.isArray(errorData.issues)) {
      const validationErrors = errorData.issues.map((issue: { path?: string[]; message?: string }) =>
        `${issue.path?.join('.') || 'field'}: ${issue.message || 'Invalid'}`
      ).join(', ');
      throw new Error(`${errorMessage} (${validationErrors})`);
    }
    throw new Error(errorMessage);
  }

  const result: UserResponse = await response.json();
  return result.data;
}

/**
 * Update existing user
 */
export async function updateUser(
  id: string,
  data: UpdateUserData,
  tenantSlug: string,
  userId?: string | number | null,
): Promise<User> {
  const response = await fetch(buildApiUrl(`${API_CONFIG.ENDPOINTS.USERS}/${id}`), {
    method: 'PUT',
    headers: getApiHeaders(tenantSlug, userId),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to update user');
  }

  const result: UserResponse = await response.json();
  return result.data;
}

/**
 * Delete user
 */
export async function deleteUser(
  id: string,
  tenantSlug: string,
  userId?: string | number | null,
): Promise<void> {
  const response = await fetch(buildApiUrl(`${API_CONFIG.ENDPOINTS.USERS}/${id}`), {
    method: 'DELETE',
    headers: getApiHeaders(tenantSlug, userId),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to delete user');
  }
}

/**
 * Upload avatar for user
 */
export async function uploadUserAvatar(
  file: File,
  userId: string | number,
  tenantSlug: string,
): Promise<{ avatarUrl: string }> {
  const result = await uploadAvatar(file, String(userId), tenantSlug);
  return { avatarUrl: result.data.avatarUrl };
}

/**
 * Fetch quick list of users
 */
export async function fetchQuickList(
  tenantSlug: string,
  userId?: string | number | null,
  search?: string,
): Promise<User[]> {
  const params = new URLSearchParams({
    page: '1',
    limit: '100',
    sortBy: 'username',
    sortOrder: 'asc',
    ...(search && { search }),
  });

  const response = await fetch(
    buildApiUrl(`${API_CONFIG.ENDPOINTS.USERS}/quicklist?${params}`),
    {
      method: 'GET',
      headers: getApiHeaders(tenantSlug, userId),
    },
  );

  if (!response.ok) {
    throw new Error('Failed to load quick list');
  }

  const result = await response.json();
  return Array.isArray(result?.data) ? result.data : [];
}

/**
 * Fetch options data (profiles, log profiles, tenants)
 */
export async function fetchUserOptions(
  tenantSlug: string,
  userId?: string | number | null,
): Promise<{
  profiles: Array<{ id: string; name: string; description?: string }>;
  logProfiles: Array<{ id: string; name: string; description?: string }>;
  tenants: Array<{ id: string; name: string; slug: string }>;
}> {
  const [profilesResponse, logProfilesResponse, tenantsResponse] = await Promise.all([
    fetch(buildApiUrl(API_CONFIG.ENDPOINTS.ROLE_PROFILES), {
      headers: getApiHeaders(tenantSlug, userId),
    }),
    fetch(buildApiUrl(API_CONFIG.ENDPOINTS.LOG_PROFILES), {
      headers: getApiHeaders(tenantSlug, userId),
    }),
    fetch(buildApiUrl(API_CONFIG.ENDPOINTS.USER_TENANTS), {
      headers: getApiHeaders(tenantSlug, userId),
    }),
  ]);

  const profilesData = profilesResponse.ok
    ? (await profilesResponse.json()).data || []
    : [];
  const logProfilesData = logProfilesResponse.ok
    ? (await logProfilesResponse.json()).data || []
    : [];
  const tenantsData = tenantsResponse.ok
    ? (await tenantsResponse.json()).data || []
    : [];

  // Convert IDs to strings for compatibility with MultiSelect components
  const profiles = profilesData.map((p: Record<string, unknown>) => ({ ...p, id: String(p.id) }));
  const logProfiles = logProfilesData.map((lp: Record<string, unknown>) => ({ ...lp, id: String(lp.id) }));
  const tenants = tenantsData.map((t: Record<string, unknown>) => ({ ...t, id: String(t.id) }));

  return { profiles, logProfiles, tenants };
}

